/**
 * @fileoverview Detail Coordinator Hook
 *
 * Bu hook, ilan detay işlemlerini koordine eder.
 * Detay modal, navigasyon ve URL yönetimini birleştirir.
 *
 * @module hooks/useDetailCoordinator
 */

import { useCallback, useEffect } from "react";
import { useListingDetail } from "./useListingDetail.js";
import { useDetailNavigation } from "./useDetailNavigation.js";
import { buildEmlakjetUrl, setOpenInEmlakjetUrl } from "./useDisplayMode.js";

/**
 * @typedef {Object} UseDetailCoordinatorParams
 * @property {Function} updateWidgetState - Widget state güncelleme fonksiyonu
 * @property {Function} requestFullscreen - Fullscreen moduna geçiş fonksiyonu
 * @property {Object} widgetState - Widget state
 * @property {Object} query - Mevcut arama sorgusu
 * @property {Function} setCallError - Hata setter
 */

/**
 * @typedef {Object} UseDetailCoordinatorReturn
 * @property {string|null} selectedListingId - Seçili ilan ID
 * @property {Object|null} listingDetail - İlan detay bilgisi
 * @property {boolean} detailLoading - Detay yükleniyor mu
 * @property {number} selectedImageIndex - Galeri'de seçili resim indeksi
 * @property {Function} setSelectedImageIndex - Resim indeksi setter
 * @property {Function} handleDetailClick - Detay tıklaması handler
 * @property {Function} handleCloseDetail - Detay kapatma handler
 */

/**
 * İlan detay işlemlerini koordine eden hook.
 *
 * Bu hook, useListingDetail ve useDetailNavigation hook'larını
 * birleştirerek detay modal yönetimini sağlar.
 *
 * @function useDetailCoordinator
 * @param {UseDetailCoordinatorParams} params - Hook parametreleri
 * @returns {UseDetailCoordinatorReturn} Detay state ve handler'ları
 *
 * @example
 * const {
 *   selectedListingId,
 *   listingDetail,
 *   handleDetailClick,
 *   handleCloseDetail,
 * } = useDetailCoordinator({
 *   updateWidgetState,
 *   requestFullscreen,
 *   widgetState,
 *   query,
 *   setCallError,
 * });
 */
export function useDetailCoordinator({
  updateWidgetState,
  requestFullscreen,
  widgetState,
  query,
  setCallError,
}) {
  // Detail fetching
  const {
    listingDetail,
    detailLoading,
    selectedImageIndex,
    setSelectedImageIndex,
    fetchDetail,
    clearDetail,
  } = useListingDetail(setCallError);

  // Navigation (URL-based)
  const {
    selectedListingId,
    handleDetailClick: navDetailClick,
    handleCloseDetail: navCloseDetail,
  } = useDetailNavigation({
    updateWidgetState,
    requestFullscreen,
    widgetState,
  });

  // Update Emlakjet URL dynamically
  useEffect(() => {
    const url = buildEmlakjetUrl(selectedListingId, query?.path, query?.filters);
    setOpenInEmlakjetUrl(url);
  }, [selectedListingId, query?.path, query?.filters]);

  /**
   * Detay tıklaması wrapper handler'ı - fetch ve state güncelleme birleşik.
   *
   * KRİTİK SIRALAMA: fetchDetail, handleDetailClick'ten ÖNCE çağrılmalıdır!
   * Sebep: setWidgetState'in async persist mekanizması mobil cihazlarda
   * sendFollowUpMessage ile çakışabilir.
   */
  const handleDetailClick = useCallback((listingId) => {
    fetchDetail(listingId);  // sendFollowUpMessage çağırır - ilk olmalı
    navDetailClick(listingId);  // State günceller - API çağrısından sonra
  }, [fetchDetail, navDetailClick]);

  /**
   * Detay kapatma wrapper handler'ı - state ve veri temizleme birleşik.
   */
  const handleCloseDetail = useCallback(() => {
    navCloseDetail();
    clearDetail();
  }, [navCloseDetail, clearDetail]);

  return {
    // State
    selectedListingId,
    listingDetail,
    detailLoading,
    selectedImageIndex,
    setSelectedImageIndex,

    // Actions
    handleDetailClick,
    handleCloseDetail,
    clearDetail,
  };
}

export default useDetailCoordinator;
